import { ConsultationDexieDb } from "../db/ConsultationDexieDb";
import { DiagnosticEvent } from "../domain/types";

export class RecoveryDiagnostics {
  private readonly inMemoryEvents: DiagnosticEvent[] = [];

  constructor(
    private readonly db: ConsultationDexieDb,
    private readonly options: {
      sessionId: string;
      limit: number;
      onEvent?: (event: DiagnosticEvent) => void;
    }
  ) {}

  async record(
    code: string,
    detail?: Record<string, unknown>,
    now = Date.now()
  ): Promise<void> {
    const event: DiagnosticEvent = {
      sessionId: this.options.sessionId,
      ts: now,
      code,
      detail,
    };

    this.inMemoryEvents.push(event);
    if (this.inMemoryEvents.length > this.options.limit) {
      this.inMemoryEvents.splice(0, this.inMemoryEvents.length - this.options.limit);
    }

    if (this.options.onEvent) {
      this.options.onEvent(event);
    }

    try {
      await this.db.diagnostics.put(event);
      await this.trimPersistent();
    } catch {
      // Swallow diagnostics persistence failures to avoid impacting capture/recovery flows.
    }
  }

  getInMemorySnapshot(): DiagnosticEvent[] {
    return this.inMemoryEvents.slice();
  }

  async getPersistentSnapshot(): Promise<DiagnosticEvent[]> {
    return this.db.diagnostics
      .where("sessionId")
      .equals(this.options.sessionId)
      .reverse()
      .limit(this.options.limit)
      .toArray();
  }

  private async trimPersistent(): Promise<void> {
    const rows = await this.db.diagnostics
      .where("sessionId")
      .equals(this.options.sessionId)
      .toArray();

    if (rows.length <= this.options.limit) {
      return;
    }

    const sorted = rows.sort((left, right) => left.ts - right.ts);
    const toDelete = sorted.slice(0, rows.length - this.options.limit);
    const ids = toDelete
      .map((row) => row.id)
      .filter((value): value is number => typeof value === "number");

    if (ids.length > 0) {
      await this.db.diagnostics.bulkDelete(ids);
    }
  }
}
